import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, FieldArray, getFormValues, reduxForm } from 'redux-form';
import { ButtonV2 } from '@zola/zola-ui';

import { RenderField, RenderSubmitButton } from '../../../common/form';
import { getTotalNumberOfSeats } from '../../../../selectors/seatingchart/SeatingChartSelector';
import * as Validations from '../../../common/form/commonValidations';
import { addNewTables } from '../../../../actions/SeatingChartActions';
import { normalizeValueInRange } from '../../../common/form/commonNormalizations';
import Loader from '../../../common/ui/Loader';
import './TableModal.less';

const RenderNewTableFields = ({ newTable, fieldIndex, fields }) => (
  <div className="add-field">
    <div className="add-field__row">
      <Field
        name={`${newTable}.name`}
        component={RenderField}
        label="Name"
        type="text"
        className="add-field__row__input"
        placeholder="Table Name"
        required
        validate={[Validations.REQUIRED, Validations.MAX_LENGTH_50]}
        maxChars={100}
        hideFormGroups
        autoFocus
      />
      <Field
        name={`${newTable}.num_seats`}
        label="Number of Seats"
        component={RenderField}
        type="number"
        pattern="[0-9]"
        className="add-field__row__input"
        required
        normalize={normalizeValueInRange(1, 99)}
        validate={Validations.REQUIRED}
        hideFormGroups
      />
    </div>
    {fieldIndex !== 0 && (
      <div role="button" className="add-field__delete">
        <i className="zolaicon zolaicon-trashcan" onClick={() => fields.remove(fieldIndex)} />
      </div>
    )}
  </div>
);

RenderNewTableFields.propTypes = {
  newTable: PropTypes.string,
  fieldIndex: PropTypes.number,
  fields: PropTypes.objectOf(PropTypes.any),
};

class NewTableFields extends React.Component {
  componentDidMount() {
    const { fields, numTables } = this.props;
    fields.push({ name: `Table ${numTables + 1}`, num_seats: 8 });
  }

  render() {
    const { fields, id, label, hideFormGroup, numTables, newTables, totalNumSeats } = this.props;
    const numSeats = fields.length > 0 ? newTables.new_tables[fields.length - 1].num_seats : 8; // Default num seats to previous entry's number of seats
    let seatMax = totalNumSeats;

    if (newTables != null) {
      newTables.new_tables.forEach(field => {
        seatMax += field.num_seats;
      });
    }

    const showWarning = fields.length + numTables + 1 >= 100 || seatMax >= 750;

    return (
      <div className={`${hideFormGroup ? '' : 'form-group'}`} style={{ position: 'relative' }}>
        <label htmlFor={id}>{label}</label>
        <div id={id} className="table-fields">
          {fields.map((newTable, index, fieldsRef) => (
            <RenderNewTableFields
              key={index}
              newTable={newTable}
              fieldIndex={index}
              fields={fieldsRef}
            />
          ))}
        </div>
        {showWarning ? (
          <div>
            {seatMax === 750 && (
              <p className="add-table-warning">You&apos;ve reached the max limit of 750 seats</p>
            )}
            {seatMax > 750 && (
              <p className="table-warning">You&apos;ve exceeded the max limit of 750 seats</p>
            )}
            {fields.length + numTables === 100 && (
              <p className="add-table-warning">You&apos;ve reached the max limit of 100 tables</p>
            )}
            {fields.length + numTables > 100 && (
              <p className="table-warning">You&apos;ve exceeded the max limit of 100 tables</p>
            )}
            <span role="button" className="add-table-disabled">
              + Add another table
            </span>
          </div>
        ) : (
          <span
            role="button"
            className="text-primary add-table"
            onClick={() =>
              fields.push({ name: `Table ${fields.length + numTables + 1}`, num_seats: numSeats })
            }
          >
            + Add another table
          </span>
        )}
      </div>
    );
  }
}

NewTableFields.propTypes = {
  fields: PropTypes.objectOf(PropTypes.any),
  id: PropTypes.string,
  label: PropTypes.string,
  hideFormGroup: PropTypes.bool,
  numTables: PropTypes.number,
  newTables: PropTypes.objectOf(PropTypes.any),
  totalNumSeats: PropTypes.number,
};

const NewTableModal = ({
  handleSubmit,
  pristine,
  submitting,
  valid,
  numTables,
  onCancel,
  newTables,
  totalNumSeats,
}) => {
  let disabled;
  let seatMax = totalNumSeats;

  if (newTables != null) {
    newTables.new_tables.forEach(field => {
      seatMax += field.num_seats;
    });
  }

  if (newTables != null) {
    disabled = newTables.new_tables.length + numTables > 100 || seatMax > 750;
  }

  return (
    <div>
      <div className="modal-header">
        <h3 className="modal-title">Add Table</h3>
      </div>
      <form onSubmit={handleSubmit} id="add-table-form" className="tableModal" noValidate>
        <FieldArray
          name="new_tables"
          component={NewTableFields}
          numTables={numTables}
          totalNumSeats={totalNumSeats}
          newTables={newTables}
          id="new-table-fields"
          lockAxis="y"
        />
        {submitting ? (
          <Loader />
        ) : (
          <div className="tableModal__buttonGroup">
            <ButtonV2 type="button" className="tableModal__delete-form" onClick={onCancel}>
              Cancel
            </ButtonV2>
            <RenderSubmitButton
              className="tableModal__saveBtn"
              pristine={pristine}
              disabled={disabled}
              submitting={submitting}
              valid={valid}
            />
          </div>
        )}
      </form>
    </div>
  );
};

NewTableModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  numTables: PropTypes.number,
  newTables: PropTypes.objectOf(PropTypes.any),
  totalNumSeats: PropTypes.number,
};

const mapStateToProps = state => ({
  newTables: getFormValues('table_form')(state),
  totalNumSeats: getTotalNumberOfSeats(state.seatingChart),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onSubmit: createTaskRequest =>
    dispatch(addNewTables(createTaskRequest, true)).then(() => {
      ownProps.hideModalFn();
    }),
  onCancel: () => {
    ownProps.hideModalFn();
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'table_form',
  })(NewTableModal)
);



// WEBPACK FOOTER //
// ./src/components/manage/seatingchart/Modals/NewTable.jsx